import { createFileRoute, Link } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { useSubmissions, useAuth } from "@/projects/lib/useStore";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/projects/components/ui/table";
import { StatusBadge } from "@/projects/components/status-badge";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/projects/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/projects/components/ui/popover";
import { Calendar } from "@/projects/components/ui/calendar";
import { Eye, Search, CalendarIcon, X } from "lucide-react";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { cn } from "@/projects/lib/utils";
import type { SubmissionStatus } from "@/projects/lib/store";

export const Route = createFileRoute("/teacher/submissions")({
  head: () => ({ meta: [{ title: "My Assignments — Teacher" }] }),
  component: TeacherSubmissions,
});

function TeacherSubmissions() {
  const auth = useAuth();
  const all = useSubmissions();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<SubmissionStatus | "All">("All");
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [quick, setQuick] = useState<"all" | "today" | "7d" | "30d">("all");

  const mine = useMemo(() => {
    const now = new Date();
    let from = fromDate;
    let to = toDate;
    if (quick !== "all") {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      if (quick === "today") from = d;
      if (quick === "7d") { const x = new Date(d); x.setDate(x.getDate() - 6); from = x; }
      if (quick === "30d") { const x = new Date(d); x.setDate(x.getDate() - 29); from = x; }
      to = now;
    }
    return all.filter((s) => s.assignedTeacherId === auth?.id).filter((s) => {
      if (status !== "All" && s.status !== status) return false;
      const submitted = new Date(s.submittedAt);
      if (from && submitted < new Date(from.getFullYear(), from.getMonth(), from.getDate())) return false;
      if (to) {
        const end = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59);
        if (submitted > end) return false;
      }
      if (q) {
        const t = q.toLowerCase();
        return s.studentName.toLowerCase().includes(t) || s.studentId.toLowerCase().includes(t) || s.videoTitle.toLowerCase().includes(t) || s.id.toLowerCase().includes(t);
      }
      return true;
    });
  }, [all, auth?.id, status, q, fromDate, toDate, quick]);

  const hasFilters = status !== "All" || q || fromDate || toDate || quick !== "all";
  const clearAll = () => { setStatus("All"); setQ(""); setFromDate(undefined); setToDate(undefined); setQuick("all"); };

  return (
    <DashboardLayout role="teacher" title="My Assigned Submissions" breadcrumb={["Home", "Assignments"]}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-64 pl-8" />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as SubmissionStatus | "All")}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            {(["All", "Assigned for Verification", "Not Proper", "Ready for Reward"] as const).map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={quick} onValueChange={(v) => { setQuick(v as typeof quick); if (v !== "all") { setFromDate(undefined); setToDate(undefined); } }}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Date range" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("justify-start text-left font-normal", !fromDate && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {fromDate ? format(fromDate, "PP") : <span>From date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={fromDate} onSelect={(d) => { setFromDate(d); setQuick("all"); }} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("justify-start text-left font-normal", !toDate && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {toDate ? format(toDate, "PP") : <span>To date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={toDate} onSelect={(d) => { setToDate(d); setQuick("all"); }} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            <X className="mr-1 h-3.5 w-3.5" /> Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-muted-foreground">{mine.length} result{mine.length === 1 ? "" : "s"}</div>
      </div>


      <div className="overflow-hidden rounded-xl border bg-card shadow-soft">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Reference</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Video Title</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mine.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-16 text-center text-sm text-muted-foreground">No submissions are currently assigned to you.</TableCell></TableRow>
              )}
              {mine.map((s) => (
                <TableRow key={s.id} className="hover:bg-muted/40">
                  <TableCell className="font-mono text-xs text-royal">{s.id}</TableCell>
                  <TableCell>
                    <div className="font-medium">{s.studentName}</div>
                    <div className="text-xs text-muted-foreground">{s.studentId}</div>
                  </TableCell>
                  <TableCell className="max-w-[240px]">
                    <div className="truncate font-medium">{s.videoTitle}</div>
                    <div className="text-xs text-muted-foreground">{s.fileSizeMB} MB</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(s.submittedAt).toLocaleDateString()}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-right">
                    <Link to="/teacher/verify/$id" params={{ id: s.id }}>
                      <Button size="sm" variant="outline"><Eye className="mr-1 h-3.5 w-3.5" /> Verify</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
}
